import delay from 'lodash/delay';
import forEach from 'lodash/forEach';
import isArray from 'lodash/isArray';
import keys from 'lodash/keys';
import memoize from 'lodash/memoize';
import once from 'lodash/once';
import range from 'lodash/range';
import isVisibleRate from '../../lib/dom/element-visibility';
import iterate from '../../lib/dom/iterate';
import onScrollProgress from '../../lib/dom/on-scroll-progress';
import { interval } from '../../vendor/splitmetrics-track';



export default ({
  trackSlideView,
  trackSlideOpen,
  trackSlideClose,
  trackSlideInaction,
  SCREENSHOT_INACTION_EVENT_NAME
}) => {
  const cacheKeyResolver = (params, isAutoEvent) => `${keys(params).sort().map((key) => params[key]).join(' ')} ${Boolean(isAutoEvent)}`;

  const calcScreenshotEventParams = (
    slideId, // element id in an array of videos and screenshots: [video1, video2, screenshot1, screenshot2]
    screenshotId, // screenshot index relating to array of screenshots: [screenshot1, screenshot2]
    isVideo,
    isFullScreen
  ) => {
    const params = {
      is_video: Boolean(isVideo),
      full_screen: Boolean(isFullScreen),
      screenshot_id: isVideo ? null : Number(screenshotId),
      video_id: isVideo ? Number(slideId) : null,
      gallery_slide_index: Number(slideId)
    };


    return params;
  };

  const _trackView = memoize(trackSlideView, cacheKeyResolver);

  const trackView = (
    videoIndexes,
    slideIds,
    params,
    isAutoEvent = false,
    _skip = false
  ) => {
    const { full_screen, gallery_slide_index } = params;

    _trackView(params, isAutoEvent);

    if (full_screen) {
      _trackView(params, isAutoEvent);
    } else {
      if (!_skip) {
        forEach(range(0, gallery_slide_index + 1, 1), (index) => {
          const isVideo = index < videoIndexes.length;

          _trackView({
            ...params,
            ...calcScreenshotEventParams(index, slideIds[index], isVideo, false)
          }, isAutoEvent);
        });
      }
    }
  };

  const galleryScreenshotWatching = (EVENT_NAME, callback) => {
    const dynamicDelay = (count) => {
      if (count <= 3) {
        return 1000;
      }

      if (count > 3 && count < 8) {
        return 2500;
      }

      return 5000;
    };

    return once(interval(dynamicDelay).onPing(callback, EVENT_NAME));
  };

  let stopScreenshotWatching = () => {};

  const startScreenshotWatching = (index, id = index) => {
    stopScreenshotWatching();

    stopScreenshotWatching = galleryScreenshotWatching(SCREENSHOT_INACTION_EVENT_NAME, (intervalParams) => {
      trackSlideInaction({
        ...intervalParams,
        ...calcScreenshotEventParams(id, null, false, true)
      });
    });
  };

  const eachVisibleScreenshot = (elements, callback) => {
    iterate(elements, (element, index) => {
      if (!isVisibleRate(element, 2 / 3)) {
        return;
      }

      callback(element, index);
    });
  };

  const initAutoTracking = (screenshotElements, videoIndexes, slideIds, screenshotSlidesOffset) => {
    let hasTracked = false;
    let hasAnyAction = false;
    let offDelay = () => {};
    let offScroll = () => {};

    const track = (screenshotParams) => {
      trackView(videoIndexes, slideIds, screenshotParams, !hasAnyAction);
      offDelay();
      offScroll();
    };

    const tryTrack = () => {
      if (hasTracked) {
        return;
      }

      eachVisibleScreenshot(screenshotElements, (element, index) => {
        track(calcScreenshotEventParams(index + screenshotSlidesOffset, false, false));
        hasTracked = true;
      });
    };

    offScroll = onScrollProgress(() => {
      hasAnyAction = true;

      tryTrack();
    });

    const delayID = delay(tryTrack, 3000);

    offDelay = () => clearInterval(delayID);

    tryTrack();
  };

  return () => {
    let screenshotImages = [];
    let screenshotImagesFullScreen = [];
    let videoScreenshotSlideIndexes = -1;
    let screenshotSlidesOffset = 0; // videos length + screenshot index relating to screenshots array

    return {
      onInit (images, fullscreenImages, videoIndexes = [], slideIds = []) {
        videoScreenshotSlideIndexes = isArray(videoIndexes) ? videoIndexes : [ videoIndexes ];
        screenshotImages = images;
        screenshotImagesFullScreen = fullscreenImages;
        screenshotSlidesOffset = videoScreenshotSlideIndexes.length;
        initAutoTracking(screenshotImages, videoScreenshotSlideIndexes, slideIds, screenshotSlidesOffset);
      },

      onTransitionEnd () {},

      onSwitchSlide (popupIsOpen, slideIndex, isVideo, slideIds) {
        stopScreenshotWatching();

        setTimeout(() => {
          const images = popupIsOpen ? screenshotImagesFullScreen : screenshotImages;


          eachVisibleScreenshot(images, (element, index) => {
            const screenshotSlideId = index + screenshotSlidesOffset;

            trackView(videoScreenshotSlideIndexes, slideIds, calcScreenshotEventParams(screenshotSlideId, slideIds[screenshotSlideId], isVideo, popupIsOpen));
          });
        }, 200);

        if (popupIsOpen) {
          startScreenshotWatching(slideIndex, slideIds[slideIndex]);
        }
      },

      onOpenPopup (slideIndex, isVideo, slideIds) {
        stopScreenshotWatching();
        const screenshotParams = calcScreenshotEventParams(slideIndex, slideIds[slideIndex], isVideo, true);

        trackSlideOpen(screenshotParams);
        trackView(videoScreenshotSlideIndexes, slideIds, screenshotParams);
        startScreenshotWatching(slideIndex, slideIds[slideIndex]);
      },

      onClosePopup (slideIndex, isVideo, slideIds) {
        stopScreenshotWatching();
        trackSlideClose(calcScreenshotEventParams(slideIndex, slideIds[slideIndex], isVideo, true));
      }
    };
  };
};



// WEBPACK FOOTER //
// ./src/lib/bind-event-tracking/_gallery.js